import { type FormEvent, useState } from 'react';

import { bootstrap, login, requestPasswordReset, type AuthenticatedUser } from '../api.js';

interface AuthScreenProps {
	bootstrapRequired: boolean;
	notice?: string;
	onAuthenticated: (user: AuthenticatedUser) => void;
}

export function AuthScreen({ bootstrapRequired, notice, onAuthenticated }: AuthScreenProps) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [resetMode, setResetMode] = useState(false);
	const [resetEmailConfigured, setResetEmailConfigured] = useState<boolean>();

	async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const data = new FormData(event.currentTarget);

		try {
			if (resetMode) {
				const result = await requestPasswordReset(String(data.get('email')));
				setResetEmailConfigured(result.emailConfigured);
				return;
			}
			const result = bootstrapRequired
				? await bootstrap({
						bootstrapToken: String(data.get('bootstrapToken')),
						displayName: String(data.get('displayName')),
						email: String(data.get('email')),
						password: String(data.get('password')),
					})
				: await login(String(data.get('email')), String(data.get('password')));
			onAuthenticated(result.user);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Authentication failed');
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="auth-page">
			<div className="auth-intro">
				<div className="brand brand-light">
					<img className="brand-icon" src="/livechat-icon.png" alt="" />
					<span>Livechat Platform</span>
				</div>
				<div>
					<p className="eyebrow eyebrow-light">Customer support workspace</p>
					<h1>Conversations under your control.</h1>
					<p>A simple, secure place to help customers and work together as a team.</p>
				</div>
				<p className="auth-footnote">
					Live conversations · Customer history · Team collaboration
				</p>
			</div>

			<div className="auth-panel">
				<form className="auth-card" onSubmit={(event) => void submit(event)}>
					<p className="eyebrow">
						{bootstrapRequired
							? 'First-time setup'
							: resetMode
								? 'Account recovery'
								: 'Welcome back'}
					</p>
					<h2>
						{bootstrapRequired
							? 'Create the platform administrator'
							: resetMode
								? 'Reset your password'
								: 'Sign in'}
					</h2>
					<p className="muted">
						{bootstrapRequired
							? 'Use the private bootstrap token stored on the server. This setup closes permanently once the account is created.'
							: resetMode
								? 'Enter your account email and we will send a single-use reset link.'
								: 'Enter your email address and password to continue.'}
					</p>

					{notice && <div className="alert success">{notice}</div>}
					{resetEmailConfigured === true && (
						<div className="alert success">
							If an active account exists for that address, a reset link has been
							sent.
						</div>
					)}
					{resetEmailConfigured === false && (
						<div className="alert error">
							Password-reset email is not configured yet. Contact the platform
							administrator for access.
						</div>
					)}
					{error && <div className="alert error">{error}</div>}

					{bootstrapRequired && (
						<>
							<label>
								<span>Your name</span>
								<input
									name="displayName"
									autoComplete="name"
									required
									maxLength={160}
								/>
							</label>
							<label>
								<span>Server bootstrap token</span>
								<input
									name="bootstrapToken"
									type="password"
									required
									autoComplete="off"
								/>
							</label>
						</>
					)}
					<label>
						<span>Email address</span>
						<input
							name="email"
							type="email"
							required
							autoComplete="email"
							maxLength={320}
						/>
					</label>
					{!resetMode && (
						<label>
							<span>Password</span>
							<input
								name="password"
								type="password"
								required
								minLength={bootstrapRequired ? 12 : 1}
								maxLength={128}
								autoComplete={
									bootstrapRequired ? 'new-password' : 'current-password'
								}
							/>
						</label>
					)}
					<button className="primary-button full-button" disabled={busy}>
						{busy
							? 'Please wait…'
							: bootstrapRequired
								? 'Secure this platform'
								: resetMode
									? 'Send reset link'
									: 'Sign in'}
					</button>
					{!bootstrapRequired && (
						<button
							type="button"
							className="auth-switch-button"
							onClick={() => {
								setError(undefined);
								setResetMode((current) => !current);
								setResetEmailConfigured(undefined);
							}}
						>
							{resetMode ? 'Back to sign in' : 'Forgot your password?'}
						</button>
					)}
				</form>
			</div>
		</div>
	);
}
